import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import WarrantyPaymentTypeRadio from '../../../../components/common/WarrantyPaymentTypeRadio';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetProformaLaborDropdownQuery } from '../../../../api/endpoints/proformaInvoice';

const ProformaLaborDialog = ({ visible, onHide, onSave, editingItem = null, chassisNo = '' }) => {
    const { th, getCurrentLocale, getCurrentLanguageId } = useLanguage();
    const initialForm = {
        itemCode: null,
        itemName: '',
        description: '',
        quantity: 1,
        laborTime: 0,
        price: 0,
        totalPrice: 0,
        warrantyType: null,
        type: 2
    };

    const [formData, setFormData] = useState(initialForm);
    const [submitted, setSubmitted] = useState(false);
    const [selectedLabor, setSelectedLabor] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterInput, setFilterInput] = useState('');
    const debounceRef = useRef(null);

    const { data: laborItems = [], isFetching } = useGetProformaLaborDropdownQuery(
        {
            languageId: getCurrentLanguageId(),
            chassisNo: chassisNo || undefined,
            searchText: searchText || undefined,
            pageSize: 20
        },
        { skip: !visible }
    );

    const laborOptions = laborItems.map(item => ({
        label: item.text,
        value: item.id,
        raw: item
    }));

    useEffect(() => {
        if (!visible) {
            setFormData(initialForm);
            setSubmitted(false);
            setSelectedLabor(null);
            setSearchText('');
            setFilterInput('');
            return;
        }
        if (editingItem) {
            setFormData({
                id: editingItem.id,
                itemCode: editingItem.itemCode || '',
                itemName: editingItem.itemName || '',
                description: editingItem.description || '',
                quantity: editingItem.quantity ?? 1,
                laborTime: editingItem.laborTime ?? 0,
                price: editingItem.price ?? 0,
                totalPrice: editingItem.totalPrice ?? 0,
                warrantyType: editingItem.warrantyType,
                type: editingItem.type ?? 2
            });
        }
    }, [visible, editingItem]);

    const getRate = useCallback((labor, warrantyType) => {
        if (!labor) return 0;
        if (warrantyType === 2) return Number(labor.warrantyRate ?? 0);
        return Number(labor.normalWarrantyRate ?? 0);
    }, []);

    const calcAmount = (laborTime, rate) => Math.round((laborTime || 0) * (rate || 0) * 100) / 100;
    const calcTotal = (qty, amount) => Math.round((qty || 0) * (amount || 0) * 100) / 100;

    const handleLaborSelect = (val) => {
        if (!val) {
            setSelectedLabor(null);
            setFormData(prev => ({ ...prev, itemCode: null, itemName: '', quantity: 1, laborTime: 0, price: 0, totalPrice: 0 }));
            return;
        }
        const opt = laborOptions.find(o => o.value === val);
        if (opt?.raw) {
            const labor = opt.raw;
            setSelectedLabor(labor);
            const rate = getRate(labor, formData.warrantyType);
            const laborTime = Number(labor.laborTime ?? 0);
            const amount = calcAmount(laborTime, rate);
            setFormData(prev => ({
                ...prev,
                itemCode: String(labor.id),
                itemName: labor.text || '',
                laborTime,
                price: amount,
                totalPrice: calcTotal(prev.quantity, amount)
            }));
        }
    };

    const handleWarrantyChange = (val) => {
        const rate = getRate(selectedLabor, val);
        setFormData(prev => {
            const amount = calcAmount(prev.laborTime, rate);
            return {
                ...prev,
                warrantyType: val,
                price: amount,
                totalPrice: calcTotal(prev.quantity, amount)
            };
        });
    };

    const handleFilterInputChange = useCallback((val) => {
        setFilterInput(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearchText((val ?? '').trim());
        }, 400);
    }, []);

    useEffect(() => {
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, []);

    const dropdownFilterTemplate = useCallback(() => (
        <div className="p-dropdown-filter-container" onMouseDown={(e) => e.stopPropagation()}>
            <InputText
                value={filterInput}
                onChange={(e) => handleFilterInputChange(e.target.value)}
                className="p-dropdown-filter w-full"
                placeholder={th('common.search') || 'Ara'}
                autoFocus
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    ), [filterInput, handleFilterInputChange, th]);

    const handleSave = () => {
        setSubmitted(true);
        if (!formData.itemCode || !formData.warrantyType) return;
        onSave({ ...formData, type: 2 });
    };

    const footer = (
        <div className="flex gap-2 justify-content-end w-full">
            <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined />
            <Button label={editingItem ? th('common.update') : th('common.create')} icon="pi pi-check" onClick={handleSave} />
        </div>
    );

    return (
        <Dialog visible={visible} style={{ width: '40vw' }} header={th('proformaInvoice.addLabor')} modal className="p-fluid" footer={footer} onHide={onHide}>
            <div className="field mb-2">
                <label className="font-bold block mb-2">{th('warrantyClaims.detail.labor.laborCatalog')} *</label>
                <Dropdown
                    value={formData.itemCode ? Number(formData.itemCode) : null}
                    onChange={(e) => handleLaborSelect(e.value)}
                    options={laborOptions}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={th('warrantyClaims.detail.labor.laborSelectPlaceholder')}
                    className={`w-full ${submitted && !formData.itemCode ? 'p-invalid' : ''}`}
                    filter
                    filterTemplate={dropdownFilterTemplate}
                    showClear
                    loading={isFetching}
                    emptyMessage={th('common.noRecords')}
                />
                {submitted && !formData.itemCode && <small className="p-error">{th('validation.required')}</small>}
            </div>

            <div className="field mb-2">
                <label className="font-bold block mb-2">{th('warrantyClaims.detail.partDialog.warrantyType')} *</label>
                <WarrantyPaymentTypeRadio
                    value={formData.warrantyType}
                    onChange={handleWarrantyChange}
                    className={submitted && !formData.warrantyType ? 'p-invalid' : ''}
                    excludeOptions={['3']}
                />
                {submitted && !formData.warrantyType && <small className="p-error">{th('validation.required')}</small>}
            </div>

            <div className="grid">
                <div className="col-12 md:col-6">
                    <div className="field mb-2">
                        <label>{th('proformaInvoice.quantity')}</label>
                        <InputNumber
                            value={formData.quantity}
                            onChange={(e) => {
                                const q = e.value || 0;
                                setFormData(prev => ({ ...prev, quantity: q, totalPrice: calcTotal(q, prev.price) }));
                            }}
                            mode="decimal" locale={getCurrentLocale()} minFractionDigits={0} maxFractionDigits={2} min={0} step={1}
                        />
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-2">
                        <label>{th('proformaInvoice.hour')}</label>
                        <InputNumber
                            value={formData.laborTime}
                            disabled
                            mode="decimal" locale={getCurrentLocale()} minFractionDigits={0} maxFractionDigits={2}
                        />
                    </div>
                </div>
            </div>

            <div className="field mb-2">
                <label>{th('warrantyClaims.detail.partDialog.description')}</label>
                <InputText value={formData.description || ''} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} />
            </div>

            <div className="field mb-2">
                <label>{th('proformaInvoice.totalPrice')}</label>
                <InputNumber
                    value={formData.totalPrice}
                    onChange={(e) => setFormData(prev => ({ ...prev, totalPrice: e.value || 0 }))}
                    mode="decimal" minFractionDigits={2} maxFractionDigits={2}
                />
            </div>
        </Dialog>
    );
};

export default ProformaLaborDialog;
